import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDataAPI } from '../../utils/fetchData';
import './UsageBar.css';

const UsageBar = () => {
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false); // Add loading guard
  const navigate = useNavigate();

  // Debug: Log when component renders
  console.log('🎯 UsageBar component rendered', { loading, usageData });

  useEffect(() => {
    loadUsageData();
    
    // Refresh usage data every 2 minutes (reduced frequency)
    const interval = setInterval(() => {
      if (!isLoadingUsage) {
        loadUsageData();
      }
    }, 120000); // 2 minutes instead of 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  const loadUsageData = async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingUsage) {
      console.log('⏸️ Usage data already loading, skipping...');
      return;
    }
    
    setIsLoadingUsage(true);
    
    try {
      const token = localStorage.getItem('dizeeToken');
      if (!token) {
        // Show fallback data if no token
        setUsageData({
          current_usage: 0,
          monthly_allowance: 300,
          percentage: 0,
          tier: 'free',
          tier_name: 'Free',
          efficiency_bonus: 0,
          overage: 0,
          overage_cost: 0
        });
        setLoading(false);
        return;
      }
      
      const response = await getDataAPI('usage/summary', token);
      
      if (response.data.success) {
        setUsageData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load usage data:', error);
      // Show fallback data on API error
      setUsageData({
        current_usage: 0,
        monthly_allowance: 300,
        percentage: 0,
        tier: 'free',
        tier_name: 'Free',
        efficiency_bonus: 0,
        overage: 0,
        overage_cost: 0
      });
    } finally {
      setLoading(false);
      setIsLoadingUsage(false); // Reset loading guard
    }
  };

  const handleUsageClick = () => {
    navigate('/usage/dashboard');
  };

  // Always show something, even when loading
  if (loading || !usageData) {
    return (
      <div className="usage-bar-container">
        <div className="usage-bar-header">
          <div className="usage-text">
            <span className="usage-current">0%</span>
            <span className="usage-label"> used</span>
          </div>
          <div className="tier-badge tier-free">
            Free
          </div>
        </div>
        <div className="usage-progress-container">
          <div className="usage-progress-bar normal" style={{ width: '0%' }} />
        </div>
        <div className="usage-details">
          <span className="usage-percentage">Ready to go</span>
        </div>
        {loading && (
          <div className="usage-hover-text">
            Loading...
          </div>
        )}
      </div>
    );
  }

  const { 
    current_usage, 
    monthly_allowance, 
    percentage, 
    tier, 
    tier_name,
    efficiency_bonus,
    overage,
    overage_cost 
  } = usageData;
  
  const isNearLimit = percentage >= 80;
  const isOverLimit = percentage >= 100;

  return (
    <div className="usage-bar-container" onClick={handleUsageClick}>
      <div className="usage-bar-header">
        <div className="usage-text">
          <span className="usage-current">{Math.round(percentage)}%</span>
          <span className="usage-label"> used</span>
        </div>
        <div className={`tier-badge tier-${tier}`}>
          {tier_name}
        </div>
      </div>
      
      <div className="usage-progress-container">
        <div 
          className={`usage-progress-bar ${
            isOverLimit ? 'over-limit' : 
            isNearLimit ? 'near-limit' : 
            'normal'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
        {isOverLimit && (
          <div 
            className="usage-progress-overage"
            style={{ width: `${Math.min(percentage - 100, 100)}%` }}
          />
        )}
      </div>
      
      <div className="usage-details">
        <span className="usage-percentage">
          {isOverLimit ? 'Over limit' : 
           isNearLimit ? 'Almost there' : 
           percentage < 10 ? 'Getting started' :
           percentage < 50 ? 'Cruising' :
           'Going strong'}
        </span>
        {efficiency_bonus > 0 && (
          <span className="efficiency-bonus">
            +{efficiency_bonus}% efficiency
          </span>
        )}
      </div>
      
      {isOverLimit && (
        <div className="usage-warning">
          Over limit - ${overage_cost.toFixed(2)} overage charges
        </div>
      )}
      
      {isNearLimit && !isOverLimit && (
        <div className="usage-warning">
          Approaching monthly limit
        </div>
      )}
      
      <div className="usage-hover-text">
        Click for usage details
      </div>
    </div>
  );
};

export default UsageBar; 